/**
 * The body of a long table, rendering only the rows near the viewport.
 *
 * Native table layout is kept, so the rows that are drawn are ordinary `tr`s:
 * spacer rows above and below stand in for the rest, and each drawn row is
 * measured, because a wrapped paragraph makes heights unknowable up front.
 *
 * @module components/ui/VirtualTableBody
 */

import React, {
  type ReactNode,
  type RefObject,
  useCallback,
  useLayoutEffect,
  useState,
} from "react";

import { useWindowedRows } from "./useWindowedRows";

/** A row of the body: a group's heading or one data row. */
export type VirtualItem<Row> =
  { kind: "group"; key: string; header: ReactNode } | { kind: "row"; key: string; row: Row };

/** What the virtual body puts on a row it draws. */
export interface RowVirtualProps {
  ref: (node: HTMLElement | null) => void;
  "data-index": number;
  "aria-rowindex": number;
}

/**
 * Past this many rows a table that scrolls on its own draws only the rows near
 * its viewport. Below it, every row is cheaper than the bookkeeping.
 */
export const VIRTUALIZE_AFTER = 100;

/**
 * The header cells' widths once a windowed table has laid out, held so they
 * stop following whichever rows happen to be drawn.
 *
 * Read when the table attaches, while its columns still size to content, then
 * only rescaled as the scroller resizes: reading again would read back the
 * widths this already fixed.
 */
export function useFrozenColumnWidths(
  scrollRef: React.RefObject<HTMLDivElement | null>,
  active: boolean,
): { widths: number[] | null; tableRef: (table: HTMLTableElement | null) => void } {
  const [widths, setWidths] = useState<number[] | null>(null);

  const tableRef = useCallback(
    (table: HTMLTableElement | null) => {
      if (!table || !active) {
        return;
      }
      const cells = table.querySelectorAll<HTMLElement>("thead th");
      setWidths(
        (current) => current ?? [...cells].map((cell) => cell.getBoundingClientRect().width),
      );
    },
    [active],
  );

  useLayoutEffect(() => {
    const scroller = scrollRef.current;
    if (!active || !scroller || typeof ResizeObserver === "undefined") {
      return undefined;
    }
    let measuredAt = scroller.clientWidth;
    const observer = new ResizeObserver(([entry]) => {
      const width = entry.contentRect.width;
      if (width !== measuredAt && measuredAt > 0) {
        const scale = width / measuredAt;
        setWidths((current) => current?.map((cellWidth) => cellWidth * scale) ?? null);
      }
      measuredAt = width;
    });
    observer.observe(scroller);
    return () => {
      observer.disconnect();
      setWidths(null);
    };
  }, [active, scrollRef]);

  return { widths: active ? widths : null, tableRef };
}

export interface VirtualTableBodyProps<Row> {
  items: readonly VirtualItem<Row>[];
  /** The element that scrolls, which is the table's own wrapper. */
  scrollRef: RefObject<HTMLDivElement | null>;
  /** A first guess at a row's height, replaced by the measured one. */
  estimateSize: number;
  span: number;
  /** Draws one item, spreading `props` on its `tr`. */
  renderItem: (item: VirtualItem<Row>, props: RowVirtualProps) => ReactNode;
}

/** Rows drawn past each edge, so a fast scroll does not show blank space. */
const OVERSCAN = 8;

export function VirtualTableBody<Row>({
  items,
  scrollRef,
  estimateSize,
  span,
  renderItem,
}: VirtualTableBodyProps<Row>): React.ReactElement {
  const getItemKey = useCallback((index: number) => items[index]?.key ?? index, [items]);
  const {
    items: drawn,
    totalSize,
    measure,
  } = useWindowedRows({
    count: items.length,
    scrollRef,
    estimateSize,
    getItemKey,
    overscan: OVERSCAN,
  });
  const before = drawn[0]?.start ?? 0;
  const after = totalSize - (drawn.at(-1)?.end ?? 0);

  return (
    <tbody>
      {before > 0 && (
        <tr aria-hidden="true" data-virtual-spacer="">
          <td colSpan={span} className="p-0" style={{ height: before }} />
        </tr>
      )}
      {drawn.map((virtual) => (
        <React.Fragment key={virtual.key}>
          {renderItem(items[virtual.index], {
            ref: measure,
            "data-index": virtual.index,
            // The header row is row 1.
            "aria-rowindex": virtual.index + 2,
          })}
        </React.Fragment>
      ))}
      {after > 0 && (
        <tr aria-hidden="true" data-virtual-spacer="">
          <td colSpan={span} className="p-0" style={{ height: after }} />
        </tr>
      )}
    </tbody>
  );
}
